import { create } from 'zustand';
import {
  AttributionTag,
  UnifiedHashtagDetail,
  DiscoveredHashtag,
  UnifiedHashtag,
  CreateAttributionTagRequest,
  UpdateAttributionTagRequest,
  ListTagsQuery,
  DiscoveredHashtagsQuery,
  UnifiedHashtagsQuery,
  PromoteHashtagRequest,
  TagPostsQuery,
  TagPost,
  PeriodType,
  TagGroup,
} from '@/types/attributionTag';
import { attributionTagService } from '@/services/attributionTagService';
import { socialMediaService } from '@/services/socialMediaService';
import { toastService, getErrorMessage } from '@/services/toastService';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';

// Helper to get customerId from global customer store
const getCustomerId = (): string | null => {
  const { selectedCustomer } = useGlobalCustomerStore.getState();
  return selectedCustomer?.id || null;
};

// Simple tag item for list display (no metrics)
interface SimpleTagItem {
  hashtag_id: number;
  hashtag_text: string;
  description: string | null;
  status: string | null;
  created_at: string | null;
}

// State interface
interface AttributionTagState {
  // Tag list state (simple list without metrics)
  simpleTagsList: SimpleTagItem[];
  simpleTagsTotal: number;
  isSimpleTagsLoading: boolean;

  // Discovered hashtags list (simple list without metrics)
  discoveredTagsList: SimpleTagItem[];
  discoveredTagsTotal: number;
  isDiscoveredTagsLoading: boolean;

  // Tag list state (with metrics - deprecated, use simpleTagsList)
  tags: AttributionTag[];
  selectedTag: UnifiedHashtagDetail | null; // Now supports both attribution tags and discovered hashtags

  // Discovered hashtags state (deprecated - use discoveredTagsList)
  discoveredHashtags: DiscoveredHashtag[];

  // Pagination
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  discoveredPagination: {
    limit: number;
    offset: number;
    total: number;
  };

  // Filters
  filters: ListTagsQuery;
  discoveredFilters: DiscoveredHashtagsQuery;

  // Date range for discovered hashtags
  discoveredDateRange: {
    start: string;
    end: string;
    period: PeriodType;
  } | null;

  // Unified hashtags state (combined view)
  unifiedHashtags: UnifiedHashtag[];
  unifiedFilters: UnifiedHashtagsQuery;
  unifiedPagination: {
    limit: number;
    offset: number;
    total: number;
  };
  unifiedDateRange: {
    start: string;
    end: string;
    period: PeriodType;
  } | null;
  isUnifiedLoading: boolean;

  // Discovered hashtags analytics state (with metrics for KPIs)
  discoveredHashtagsAnalytics: {
    summary: {
      totalHashtags: number;
      totalPosts: number;
      totalEmv: string;
      avgEmv: string;
    };
    hashtags: Array<{
      hashtagId: number;
      hashtag: string;
      postCount: number;
      totalViews: number;
      totalLikes: number;
      totalComments: number;
      totalShares: number;
      totalEmv: string;
      totalEmvValue: number;
      previousEmvValue: number;
    }>;
  } | null;
  isDiscoveredAnalyticsLoading: boolean;

  // Attribution tags analytics state (with metrics for KPIs)
  attributionTagsAnalytics: {
    summary: {
      totalTags: number;
      activeTags: number;
      totalPosts: number;
      totalEmv: string;
      avgEmv: string;
    };
    tags: Array<{
      kind: 'group' | 'tag';
      effectiveTagId: string;
      groupId: string | null;
      memberCount: number;
      hashtagId: number | null;
      hashtag: string;
      description?: string | null;
      status: string;
      postCount: number;
      totalViews: number;
      totalLikes: number;
      totalComments: number;
      totalShares: number;
      totalEmv: string;
      totalEmvValue: number;
      previousEmvValue: number;
      accountCount: number;
    }>;
  } | null;
  isAttributionAnalyticsLoading: boolean;

  // Loading states
  isLoading: boolean;
  isDetailLoading: boolean;
  isDiscoveredLoading: boolean;
  isSaving: boolean;

  // Error state
  error: string | null;

  // UI state
  isCreateDrawerOpen: boolean;
  isEditDrawerOpen: boolean;
  isPromoteDrawerOpen: boolean;
  selectedHashtagForPromotion: DiscoveredHashtag | null;

}

// Actions interface
interface AttributionTagActions {
  // Simple tags list (no metrics, alphabetical)
  loadSimpleTagsList: (type?: 'attribution' | 'discovered') => Promise<void>;

  // Discovered hashtags list (simple list without metrics)
  loadDiscoveredTagsList: () => Promise<void>;

  // Tag list actions (deprecated - use loadUnifiedHashtags instead)
  setFilters: (filters: Partial<ListTagsQuery>) => void;
  resetFilters: () => void;

  // Tag detail actions
  loadTagDetail: (tagId: string | number, query?: { period?: PeriodType; customStartDate?: string; customEndDate?: string }) => Promise<void>;
  loadTagBasicDetail: (tagId: string | number) => Promise<void>;
  clearSelectedTag: () => void;

  // CRUD actions
  createTag: (data: CreateAttributionTagRequest) => Promise<AttributionTag | null>;
  updateTag: (tagId: string | number, data: UpdateAttributionTagRequest) => Promise<AttributionTag | null>;
  deleteTag: (tagId: string | number) => Promise<boolean>;

  // Discovered hashtags
  loadDiscoveredHashtags: (query?: DiscoveredHashtagsQuery) => Promise<void>;
  setDiscoveredFilters: (filters: Partial<DiscoveredHashtagsQuery>) => void;
  promoteHashtag: (data: PromoteHashtagRequest) => Promise<AttributionTag | null>;

  // Tag grouping (mutations return the group; caller reloads the list afterward)
  createTagGroup: (name: string, tagIds: number[]) => Promise<TagGroup | null>;
  renameTagGroup: (groupId: string, name: string) => Promise<TagGroup | null>;
  addTagGroupMembers: (groupId: string, tagIds: number[]) => Promise<TagGroup | null>;
  removeTagGroupMembers: (groupId: string, tagIds: number[]) => Promise<TagGroup | null>;
  deleteTagGroup: (groupId: string) => Promise<boolean>;
  loadTagGroupDetail: (
    groupId: string,
    query?: { period?: PeriodType; customStartDate?: string; customEndDate?: string }
  ) => Promise<TagGroup | null>;
  loadTagGroupPosts: (groupId: string, query?: TagPostsQuery) => Promise<TagPost[]>;

  // Unified hashtags (combined view)
  loadUnifiedHashtags: (query?: UnifiedHashtagsQuery) => Promise<void>;
  setUnifiedFilters: (filters: Partial<UnifiedHashtagsQuery>) => void;

  // Discovered hashtags analytics (with metrics)
  loadDiscoveredHashtagsAnalytics: (period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom', customDateRange?: { start: string; end: string } | null) => Promise<void>;

  // Attribution tags analytics (with metrics - outer query to include all tags)
  loadAttributionTagsAnalytics: (period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom', customDateRange?: { start: string; end: string } | null) => Promise<void>;

  // Tag posts
  loadTagPosts: (tagId: string | number, query?: TagPostsQuery) => Promise<TagPost[]>;

  // UI state
  openCreateDrawer: () => void;
  closeCreateDrawer: () => void;
  openEditDrawer: () => void;
  closeEditDrawer: () => void;
  openPromoteDrawer: (hashtag: DiscoveredHashtag) => void;
  closePromoteDrawer: () => void;

  // Utility
  setError: (error: string | null) => void;
  reset: () => void;
}

// Initial state
const initialState: AttributionTagState = {
  simpleTagsList: [],
  simpleTagsTotal: 0,
  isSimpleTagsLoading: false,
  discoveredTagsList: [],
  discoveredTagsTotal: 0,
  isDiscoveredTagsLoading: false,
  tags: [],
  selectedTag: null,
  discoveredHashtags: [],
  pagination: { limit: 20, offset: 0, total: 0 },
  discoveredPagination: { limit: 20, offset: 0, total: 0 },
  filters: {
    sort_by: 'created_at',
    sort_order: 'desc',
    limit: 20,
    offset: 0,
  },
  discoveredFilters: {
    sort_by: 'emv',
    sort_order: 'desc',
    period: '30days',
    limit: 20,
    offset: 0,
  },
  discoveredDateRange: null,
  unifiedHashtags: [],
  unifiedFilters: {
    type: 'all',
    sort_by: 'emv',
    sort_order: 'desc',
    period: '30days',
    limit: 20,
    offset: 0,
  },
  unifiedPagination: { limit: 20, offset: 0, total: 0 },
  unifiedDateRange: null,
  isUnifiedLoading: false,
  discoveredHashtagsAnalytics: null,
  isDiscoveredAnalyticsLoading: false,
  attributionTagsAnalytics: null,
  isAttributionAnalyticsLoading: false,
  isLoading: false,
  isDetailLoading: false,
  isDiscoveredLoading: false,
  isSaving: false,
  error: null,
  isCreateDrawerOpen: false,
  isEditDrawerOpen: false,
  isPromoteDrawerOpen: false,
  selectedHashtagForPromotion: null,
};

// Create the store
export const useAttributionTagStore = create<AttributionTagState & AttributionTagActions>((set, get) => ({
  ...initialState,

  // ==========================================
  // Simple Tags List (no metrics, alphabetical)
  // ==========================================

  loadSimpleTagsList: async (type: 'attribution' | 'discovered' = 'attribution') => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ simpleTagsList: [], simpleTagsTotal: 0, isSimpleTagsLoading: false });
      return;
    }
    try {
      set({ isSimpleTagsLoading: true, error: null });
      const response = await attributionTagService.getHashtagsList(customerId, type);
      set({
        simpleTagsList: response.data || [],
        simpleTagsTotal: response.total || 0,
        isSimpleTagsLoading: false,
      });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSimpleTagsLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  loadDiscoveredTagsList: async () => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ discoveredTagsList: [], discoveredTagsTotal: 0, isDiscoveredTagsLoading: false });
      return;
    }
    try {
      set({ isDiscoveredTagsLoading: true, error: null });
      const response = await attributionTagService.getHashtagsList(customerId, 'discovered');
      set({
        discoveredTagsList: response.data || [],
        discoveredTagsTotal: response.total || 0,
        isDiscoveredTagsLoading: false,
      });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isDiscoveredTagsLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  // ==========================================
  // Tag List Actions (deprecated - use loadUnifiedHashtags instead)
  // ==========================================

  // loadTags has been removed - use loadUnifiedHashtags with type='attribution' filter instead

  setFilters: (filters) => {
    // Only reset offset to 0 if we're changing actual filters (not just pagination)
    const isOnlyOffsetChange = Object.keys(filters).length === 1 && 'offset' in filters;
    set((state) => ({
      filters: {
        ...state.filters,
        ...filters,
        // Reset offset to 0 only when changing filters (not when paginating)
        offset: isOnlyOffsetChange ? (filters.offset ?? 0) : 0,
      },
    }));
    // Use unified hashtags with attribution filter
    get().loadUnifiedHashtags({ type: 'attribution' });
  },

  resetFilters: () => {
    set({ filters: initialState.filters });
    // Use unified hashtags with attribution filter
    get().loadUnifiedHashtags({ type: 'attribution' });
  },

  // ==========================================
  // Tag Detail Actions
  // ==========================================

  loadTagDetail: async (tagId, query) => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ selectedTag: null, isDetailLoading: false });
      return;
    }
    try {
      // Clear previous tag data and start loading
      set({ selectedTag: null, isDetailLoading: true, error: null });
      // Use the new unified endpoint that works for both attribution tags and discovered hashtags
      // Pass through period/date range params so metrics are scoped to the same range as the list
      const response = await attributionTagService.getHashtagById(tagId, customerId, query);
      set({ selectedTag: response.data, isDetailLoading: false });
    } catch (error) {
      const { message } = getErrorMessage(error);
      const isNotFoundError = (message || '').toLowerCase().includes('not found');

      // If "not found" (tag has no posts in period), try to get basic info with accounts
      if (isNotFoundError) {
        try {
          const basicResponse = await attributionTagService.getHashtagBasicById(tagId, customerId);
          set({ selectedTag: basicResponse.data, isDetailLoading: false, error: null });
          return;
        } catch {
          // Basic endpoint also failed - set null but don't show error
          set({ selectedTag: null, isDetailLoading: false, error: null });
          return;
        }
      }

      set({ selectedTag: null, isDetailLoading: false, error: message });
      toastService.error('Error', message);
    }
  },

  loadTagBasicDetail: async (tagId) => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ selectedTag: null, isDetailLoading: false });
      return;
    }
    try {
      set({ isDetailLoading: true, error: null });
      // Use the basic endpoint (no metrics) for the edit view
      // This avoids 404 when a hashtag has no posts in the current period
      const response = await attributionTagService.getHashtagBasicById(tagId, customerId);
      set({ selectedTag: response.data, isDetailLoading: false });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isDetailLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  clearSelectedTag: () => set({ selectedTag: null }),

  // ==========================================
  // CRUD Actions
  // ==========================================

  createTag: async (data) => {
    const customerId = getCustomerId();
    if (!customerId) {
      toastService.error('Error', 'No customer selected');
      return null;
    }
    try {
      set({ isSaving: true, error: null });
      // Include customerId in the request data
      const response = await attributionTagService.createTag({ ...data, customerId });
      toastService.success('Success', 'Attribution tag created successfully');
      set({ isSaving: false, isCreateDrawerOpen: false });
      get().loadSimpleTagsList();
      get().loadUnifiedHashtags();
      return response.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  updateTag: async (tagId, data) => {
    const customerId = getCustomerId();
    if (!customerId) {
      toastService.error('Error', 'No customer selected');
      return null;
    }
    try {
      set({ isSaving: true, error: null });
      const response = await attributionTagService.updateTag(String(tagId), customerId, data);
      toastService.success('Success', 'Attribution tag updated successfully');
      set({ isSaving: false, isEditDrawerOpen: false });
      get().loadSimpleTagsList();
      get().loadUnifiedHashtags();
      // Refresh detail if it's the same tag
      if (String(get().selectedTag?.hashtag_id) === String(tagId)) {
        get().loadTagDetail(tagId);
      }
      return response.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  deleteTag: async (tagId) => {
    const customerId = getCustomerId();
    if (!customerId) {
      toastService.error('Error', 'No customer selected');
      return false;
    }
    try {
      set({ isSaving: true, error: null });
      await attributionTagService.deleteTag(String(tagId), customerId);
      toastService.success('Success', 'Attribution tag retired successfully');
      set({ isSaving: false });
      // Clear selected tag if it's the one being deleted
      if (String(get().selectedTag?.hashtag_id) === String(tagId)) {
        set({ selectedTag: null });
      }
      get().loadSimpleTagsList();
      get().loadUnifiedHashtags();
      return true;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return false;
    }
  },

  // ==========================================
  // Discovered Hashtags
  // ==========================================

  loadDiscoveredHashtags: async (query) => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ discoveredHashtags: [], isDiscoveredLoading: false });
      return;
    }
    try {
      set({ isDiscoveredLoading: true, error: null });
      const mergedQuery = { ...get().discoveredFilters, ...query };
      const response = await attributionTagService.getDiscoveredHashtags(customerId, mergedQuery);
      set({
        discoveredHashtags: response.data || [],
        discoveredPagination: response.pagination || { limit: 20, offset: 0, total: 0 },
        discoveredDateRange: response.dateRange || null,
        isDiscoveredLoading: false,
      });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isDiscoveredLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  setDiscoveredFilters: (filters) => {
    // Only reset offset to 0 if we're changing actual filters (not just pagination)
    const isOnlyOffsetChange = Object.keys(filters).length === 1 && 'offset' in filters;
    set((state) => ({
      discoveredFilters: {
        ...state.discoveredFilters,
        ...filters,
        // Reset offset to 0 only when changing filters (not when paginating)
        offset: isOnlyOffsetChange ? (filters.offset ?? 0) : 0,
      },
    }));
    get().loadDiscoveredHashtags();
  },

  promoteHashtag: async (data) => {
    const customerId = getCustomerId();
    if (!customerId) {
      toastService.error('Error', 'No customer selected');
      return null;
    }
    try {
      set({ isSaving: true, error: null });
      // Include customerId in the request data
      const response = await attributionTagService.promoteHashtag({ ...data, customerId });
      toastService.success('Success', `Hashtag '${data.hashtag_text}' promoted to Attribution Tag`);
      set({
        isSaving: false,
        isPromoteDrawerOpen: false,
        selectedHashtagForPromotion: null,
      });
      // Refresh all lists
      get().loadSimpleTagsList();
      get().loadDiscoveredTagsList();
      get().loadUnifiedHashtags();
      return response.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  // ==========================================
  // Tag Grouping
  // ==========================================

  createTagGroup: async (name, tagIds) => {
    const customerId = getCustomerId();
    if (!customerId) { toastService.error('Error', 'No customer selected'); return null; }
    try {
      set({ isSaving: true, error: null });
      const res = await attributionTagService.createTagGroup(customerId, name, tagIds);
      toastService.success('Success', `Group '${name}' created`);
      set({ isSaving: false });
      return res.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  renameTagGroup: async (groupId, name) => {
    const customerId = getCustomerId();
    if (!customerId) { toastService.error('Error', 'No customer selected'); return null; }
    try {
      set({ isSaving: true, error: null });
      const res = await attributionTagService.renameTagGroup(groupId, customerId, name);
      toastService.success('Success', 'Group renamed');
      set({ isSaving: false });
      return res.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  addTagGroupMembers: async (groupId, tagIds) => {
    const customerId = getCustomerId();
    if (!customerId) { toastService.error('Error', 'No customer selected'); return null; }
    try {
      set({ isSaving: true, error: null });
      const res = await attributionTagService.addTagGroupMembers(groupId, customerId, tagIds);
      toastService.success('Success', 'Tags added to group');
      set({ isSaving: false });
      return res.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  removeTagGroupMembers: async (groupId, tagIds) => {
    const customerId = getCustomerId();
    if (!customerId) { toastService.error('Error', 'No customer selected'); return null; }
    try {
      set({ isSaving: true, error: null });
      const res = await attributionTagService.removeTagGroupMembers(groupId, customerId, tagIds);
      toastService.success('Success', 'Tags removed from group');
      set({ isSaving: false });
      return res.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return null;
    }
  },

  deleteTagGroup: async (groupId) => {
    const customerId = getCustomerId();
    if (!customerId) { toastService.error('Error', 'No customer selected'); return false; }
    try {
      set({ isSaving: true, error: null });
      await attributionTagService.deleteTagGroup(groupId, customerId);
      toastService.success('Success', 'Group deleted');
      set({ isSaving: false });
      return true;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isSaving: false, error: message });
      toastService.error(title, message);
      return false;
    }
  },

  loadTagGroupDetail: async (groupId, query) => {
    const customerId = getCustomerId();
    if (!customerId) return null;
    try {
      const res = await attributionTagService.getTagGroup(groupId, customerId, query);
      return res.data;
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
      return null;
    }
  },

  loadTagGroupPosts: async (groupId, query) => {
    const customerId = getCustomerId();
    if (!customerId) return [];
    try {
      const res = await attributionTagService.getTagGroupPosts(groupId, customerId, query);
      return res.data || [];
    } catch (error) {
      const { message } = getErrorMessage(error);
      const isNotFound = (message || '').toLowerCase().includes('not found');
      if (!isNotFound) toastService.error('Error', message);
      return [];
    }
  },

  // ==========================================
  // Unified Hashtags (Combined View)
  // ==========================================

  loadUnifiedHashtags: async (query) => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ unifiedHashtags: [], isUnifiedLoading: false });
      return;
    }
    try {
      set({ isUnifiedLoading: true, error: null });
      const mergedQuery = { ...get().unifiedFilters, ...query };
      const response = await attributionTagService.getAllHashtags(customerId, mergedQuery);

      // Map DiscoveredHashtag to UnifiedHashtag using is_tracked
      const unifiedData = (response.data || []).map((hashtag) => ({
        // Use hashtag_id as string for the primary id
        id: String(hashtag.hashtag_id),
        hashtag_text: hashtag.hashtag_text,
        type: hashtag.is_tracked ? 'attribution' : 'discovered',
        account_count: hashtag.account_count,
        post_count: hashtag.post_count,
        total_emv: hashtag.total_emv,
        total_engagement: hashtag.total_engagement,
        hashtag_id: hashtag.hashtag_id,
        // Attribution tag specific fields (when is_tracked=true)
        tag_id: hashtag.is_tracked ? String(hashtag.hashtag_id) : undefined,
        status: hashtag.status || undefined,
        description: hashtag.description || undefined,
        created_at: hashtag.created_at || undefined,
        updated_at: hashtag.updated_at || undefined,
      } as UnifiedHashtag));

      // Note: Type and status filtering is done server-side via query params

      set({
        unifiedHashtags: unifiedData,
        unifiedPagination: response.pagination || { limit: 20, offset: 0, total: 0 },
        unifiedDateRange: response.dateRange || null,
        isUnifiedLoading: false,
      });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isUnifiedLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  setUnifiedFilters: (filters) => {
    // Only reset offset to 0 if we're changing actual filters (not just pagination)
    const isOnlyOffsetChange = Object.keys(filters).length === 1 && 'offset' in filters;
    set((state) => ({
      unifiedFilters: {
        ...state.unifiedFilters,
        ...filters,
        // Reset offset to 0 only when changing filters (not when paginating)
        offset: isOnlyOffsetChange ? (filters.offset ?? 0) : 0,
      },
    }));
    get().loadUnifiedHashtags();
  },

  // ==========================================
  // Discovered Hashtags Analytics (with metrics for KPIs)
  // ==========================================

  loadDiscoveredHashtagsAnalytics: async (period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom' = 'custom', customDateRange: { start: string; end: string } | null = (() => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 360); return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] }; })()) => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ discoveredHashtagsAnalytics: null, isDiscoveredAnalyticsLoading: false });
      return;
    }
    try {
      set({ isDiscoveredAnalyticsLoading: true, error: null });

      // Use socialMediaService.getHashtagAnalytics to get metrics
      // Pass excludeTracked=true to filter out promoted hashtags (attribution tags)
      const response = await socialMediaService.getHashtagAnalytics({
        customerId,
        period,
        limit: 100,
        excludeTracked: true,
        ...(period === 'custom' && customDateRange && {
          customStartDate: customDateRange.start,
          customEndDate: customDateRange.end,
        }),
      });

      if (response.data?.hashtags) {
        // Format the hashtags data
        const formattedHashtags = response.data.hashtags.map((hashtag: any) => ({
          hashtagId: hashtag.hashtagId,
          hashtag: hashtag.hashtag,
          postCount: hashtag.current.postCount,
          totalViews: hashtag.current.totalViews,
          totalLikes: hashtag.current.totalLikes,
          totalComments: hashtag.current.totalComments,
          totalShares: hashtag.current.totalShares,
          totalEmv: hashtag.current.totalEmvFormatted || hashtag.current.totalEmv,
          totalEmvValue: typeof hashtag.current.totalEmv === 'string'
            ? parseFloat(hashtag.current.totalEmv.replace(/[$,]/g, '')) || 0
            : parseFloat(hashtag.current.totalEmv) || 0,
          previousEmvValue: hashtag.previous?.totalEmv
            ? (typeof hashtag.previous.totalEmv === 'string'
                ? parseFloat(hashtag.previous.totalEmv.replace(/[$,]/g, '')) || 0
                : parseFloat(hashtag.previous.totalEmv) || 0)
            : 0,
        }));

        // Calculate summary from the data
        const totalPosts = formattedHashtags.reduce((sum: number, h: any) => sum + h.postCount, 0);
        const totalEmvValue = formattedHashtags.reduce((sum: number, h: any) => sum + h.totalEmvValue, 0);
        const avgEmvValue = formattedHashtags.length > 0 ? totalEmvValue / formattedHashtags.length : 0;

        set({
          discoveredHashtagsAnalytics: {
            summary: {
              totalHashtags: formattedHashtags.length,
              totalPosts,
              totalEmv: `$${totalEmvValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              avgEmv: `$${avgEmvValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            hashtags: formattedHashtags,
          },
          isDiscoveredAnalyticsLoading: false,
        });
      } else {
        set({
          discoveredHashtagsAnalytics: {
            summary: { totalHashtags: 0, totalPosts: 0, totalEmv: '$0.00', avgEmv: '$0.00' },
            hashtags: [],
          },
          isDiscoveredAnalyticsLoading: false,
        });
      }
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isDiscoveredAnalyticsLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  // ==========================================
  // Attribution Tags Analytics (with metrics for KPIs)
  // Uses LEFT OUTER JOIN pattern: all attribution tags + metrics where available
  // ==========================================

  loadAttributionTagsAnalytics: async (period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom' = 'custom', customDateRange: { start: string; end: string } | null = (() => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 360); return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] }; })()) => {
    const customerId = getCustomerId();
    if (!customerId) {
      set({ attributionTagsAnalytics: null, isAttributionAnalyticsLoading: false });
      return;
    }
    try {
      set({ isAttributionAnalyticsLoading: true, error: null });

      // Single source: the CRO attribution list. Returns a mix of GROUP entries (collapsed
      // spelling/case variants) and solo TAG entries, with canonical, group-deduped,
      // override-aware metrics from view_attribution_tag_posts. Replaces the old 3-source
      // name-matched merge (which double-counted case variants and knew nothing about groups).
      const listResponse = await attributionTagService.getAttributionList(customerId, {
        period,
        limit: 100,
        ...(period === 'custom' && customDateRange && {
          customStartDate: customDateRange.start,
          customEndDate: customDateRange.end,
        }),
      });

      const mergedTags = (listResponse.data || []).map((e: any) => {
        const emvValue = Number(e.total_emv) || 0;
        return {
          kind: (e.kind === 'group' ? 'group' : 'tag') as 'group' | 'tag',
          effectiveTagId: e.effective_tag_id,
          groupId: e.group_id ?? null,
          memberCount: Number(e.member_count) || 1,
          hashtagId: e.hashtag_id ?? null,
          hashtag: e.hashtag_text ?? e.name,
          description: e.description ?? null,
          status: e.status || 'Active',
          accountCount: Number(e.account_count) || 0,
          postCount: Number(e.post_count) || 0,
          totalViews: Number(e.total_views) || 0,
          totalLikes: Number(e.total_likes) || 0,
          totalComments: Number(e.total_comments) || 0,
          totalShares: Number(e.total_shares) || 0,
          totalEmv: `$${emvValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          totalEmvValue: emvValue,
          previousEmvValue: 0,
        };
      });

      // Summary totals (groups count as one entry and as active containers)
      const totalTags = mergedTags.length;
      const activeTags = mergedTags.filter((t) => t.kind === 'group' || t.status === 'Active').length;
      const totalPosts = mergedTags.reduce((sum, t) => sum + t.postCount, 0);
      const totalEmvValue = mergedTags.reduce((sum, t) => sum + t.totalEmvValue, 0);
      const avgEmvValue = totalTags > 0 ? totalEmvValue / totalTags : 0;

      set({
        attributionTagsAnalytics: {
          summary: {
            totalTags,
            activeTags,
            totalPosts,
            totalEmv: `$${totalEmvValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            avgEmv: `$${avgEmvValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          tags: mergedTags,
        },
        isAttributionAnalyticsLoading: false,
      });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({ isAttributionAnalyticsLoading: false, error: message });
      toastService.error(title, message);
    }
  },

  // ==========================================
  // Tag Posts
  // ==========================================

  loadTagPosts: async (tagId, query) => {
    const customerId = getCustomerId();
    if (!customerId) {
      return [];
    }
    try {
      // Use the new unified endpoint that works for both attribution tags and discovered hashtags
      const response = await attributionTagService.getHashtagPostsById(tagId, customerId, query);
      return response.data || [];
    } catch (error) {
      const { message } = getErrorMessage(error);
      // Don't show toast for "not found" errors - this happens when a tag has no posts
      // in the selected period. The drawer will show empty posts which is expected.
      const isNotFoundError = (message || '').toLowerCase().includes('not found');
      if (!isNotFoundError) {
        toastService.error('Error', message);
      }
      return [];
    }
  },

  // ==========================================
  // UI State
  // ==========================================

  openCreateDrawer: () => set({ isCreateDrawerOpen: true }),
  closeCreateDrawer: () => set({ isCreateDrawerOpen: false }),

  openEditDrawer: () => set({ isEditDrawerOpen: true }),
  closeEditDrawer: () => set({ isEditDrawerOpen: false }),

  openPromoteDrawer: (hashtag) => set({
    selectedHashtagForPromotion: hashtag,
    isPromoteDrawerOpen: true,
  }),
  closePromoteDrawer: () => set({
    isPromoteDrawerOpen: false,
    selectedHashtagForPromotion: null,
  }),

  // ==========================================
  // Utility
  // ==========================================

  setError: (error) => set({ error }),

  reset: () => set(initialState),
}));

export default useAttributionTagStore;
